import { getNewDefaultBoard, mainBoardLayout } from "./communicationBoardDefaults";
import type {
  CommunicationBoardData,
  CategoryCell,
  UtteranceCell,
} from "./communicationBoardTypes";

type Lang = "he" | "en" | "ru" | "ar";

// Gender-aware name mapping for green verb categories (Hebrew only)
const GREEN_CATEGORY_GENDER_NAMES: Record<string, { male: string; female: string }> = {};

// Build the mapping from mainBoardLayout
for (const row of mainBoardLayout) {
  for (const item of row) {
    if (item && item.type === "category" && item.color === "green" && item.nameFemale?.he) {
      GREEN_CATEGORY_GENDER_NAMES[item.id] = {
        male: item.name.he,
        female: item.nameFemale.he,
      };
    }
  }
}

// Arabic green category name corrections (replace old placeholder names with proper verbs).
// Maps category id -> { from: legacy incorrect Arabic name, to: correct Arabic verb }.
const AR_GREEN_NAME_FIXES: Record<string, { from: string; to: string }> = {
  "cat-want": { from: "الـ", to: "أريد" },
  "cat-need": { from: "هذا", to: "أحتاج" },
  "cat-feel": { from: "ذلك", to: "أشعر" },
  "cat-think": { from: "مع", to: "أعتقد" },
  "cat-love": { from: "و", to: "أحب" },
  "cat-dont-like": { from: "إذا", to: "لا أحب" },
};

function migrateArabicGreenNames(
  boards: CommunicationBoardData[],
  language: Lang
): { boards: CommunicationBoardData[]; changed: boolean } {
  if (language !== "ar") return { boards, changed: false };
  let changed = false;
  const migrated = boards.map((board) => {
    let boardChanged = false;
    const newCells = board.cells.map((row) =>
      row.map((cell) => {
        if (!cell || cell.type !== "category") return cell;
        const fix = AR_GREEN_NAME_FIXES[cell.id];
        if (!fix) return cell;
        if (cell.name === fix.from) {
          boardChanged = true;
          return { ...cell, name: fix.to };
        }
        return cell;
      })
    );
    if (!boardChanged) return board;
    changed = true;
    return { ...board, cells: newCells, updatedAt: new Date().toISOString() };
  });
  return { boards: migrated, changed };
}

// Arabic connector text corrections (row 4 gray cells). Map id -> { from, to }.
const AR_CONNECTOR_FIXES: Record<string, { from: string; to: string }> = {
  "conn-et": { from: "هو", to: "الـ" },
  "conn-this": { from: "أحتاج", to: "هذا" },
  "conn-that": { from: "أشعر", to: "ذلك" },
  "conn-with": { from: "أعتقد", to: "مع" },
  "conn-and": { from: "أحب", to: "و" },
  "conn-if": { from: "لا أحب", to: "إذا" },
};

function migrateArabicConnectors(
  boards: CommunicationBoardData[],
  language: Lang
): { boards: CommunicationBoardData[]; changed: boolean } {
  if (language !== "ar") return { boards, changed: false };
  let changed = false;
  const migrated = boards.map((board) => {
    let boardChanged = false;
    const newCells = board.cells.map((row) =>
      row.map((cell) => {
        if (!cell || cell.type !== "utterance") return cell;
        const fix = AR_CONNECTOR_FIXES[cell.id];
        if (!fix) return cell;
        if (cell.text === fix.from) {
          boardChanged = true;
          return { ...cell, text: fix.to };
        }
        return cell;
      })
    );
    if (!boardChanged) return board;
    changed = true;
    return { ...board, cells: newCells, updatedAt: new Date().toISOString() };
  });
  return { boards: migrated, changed };
}

// Russian connector text corrections (row 4 gray cells). Map id -> { from, to }.
const RU_CONNECTOR_FIXES: Record<string, { from: string; to: string }> = {
  "conn-et": { from: "ну", to: "этот" },
  "pronoun-you-f": { from: "ты", to: "мы" },
};

// English cell text corrections. Map id -> { from, to }.
const EN_CELL_FIXES: Record<string, { from: string; to: string }> = {
  "pronoun-you-f": { from: "you", to: "we" },
};

function migrateEnglishCellTexts(
  boards: CommunicationBoardData[],
  language: Lang
): { boards: CommunicationBoardData[]; changed: boolean } {
  if (language !== "en") return { boards, changed: false };
  let changed = false;
  const migrated = boards.map((board) => {
    let boardChanged = false;
    const newCells = board.cells.map((row) =>
      row.map((cell) => {
        if (!cell || cell.type !== "utterance") return cell;
        let updated: any = cell;
        const fix = EN_CELL_FIXES[cell.id];
        if (fix && updated.text === fix.from) {
          updated = { ...updated, text: fix.to };
          boardChanged = true;
        }
        // Strip Hebrew speechText that leaked into non-Hebrew boards (causes TTS to read Hebrew, e.g. "you feminine singular")
        if (updated.speechText && /[\u0590-\u05FF]/.test(updated.speechText)) {
          const { speechText: _omit, ...rest } = updated;
          updated = rest;
          boardChanged = true;
        }
        return updated;
      })
    );
    if (!boardChanged) return board;
    changed = true;
    return { ...board, cells: newCells, updatedAt: new Date().toISOString() };
  });
  return { boards: migrated, changed };
}

function migrateRussianConnectors(
  boards: CommunicationBoardData[],
  language: Lang
): { boards: CommunicationBoardData[]; changed: boolean } {
  if (language !== "ru") return { boards, changed: false };
  let changed = false;
  const migrated = boards.map((board) => {
    let boardChanged = false;
    const newCells = board.cells.map((row) =>
      row.map((cell) => {
        if (!cell || cell.type !== "utterance") return cell;
        const fix = RU_CONNECTOR_FIXES[cell.id];
        if (!fix) return cell;
        if (cell.text === fix.from) {
          boardChanged = true;
          return { ...cell, text: fix.to };
        }
        return cell;
      })
    );
    if (!boardChanged) return board;
    changed = true;
    return { ...board, cells: newCells, updatedAt: new Date().toISOString() };
  });
  return { boards: migrated, changed };
}

// Arabic cat-think inner items: replace legacy strings with corrected grammar.
const AR_THINK_REPLACEMENTS: Record<string, string> = {
  "هذا جيد": "أنه جيد",
  "هذا ليس جيد": "أنه ليس جيدًا",
  "هذا سيء جداً": "أنه سيئ جدًا",
  "عنك": "أفكر فيك",
  "فكرة جيدة": "أنها فكرة جيدة",
  "فكرة سيئة": "أنها فكرة سيئة",
  "نعم": "ذلك",
  "لا": "لا ذلك",
  "هذا مثير": "أنه مثير للاهتمام",
  "ليس مثير": "أنه غير مثير للاهتمام",
  "يمكن بطريقة أخرى": "أن هناك طريقة أخرى",
};

// Arabic cat-love inner items: replace legacy pronouns with verb forms.
const AR_LOVE_REPLACEMENTS: Record<string, string> = {
  "إياك": "أحبك",
  "إياه": "أحبه",
  "إياها": "أحبها",
  "إياكم": "أحبكم جميعًا",
  "هكذا": "أحبه",
};

function migrateArabicThinkItems(
  boards: CommunicationBoardData[],
  language: Lang
): { boards: CommunicationBoardData[]; changed: boolean } {
  if (language !== "ar") return { boards, changed: false };
  let changed = false;
  const migrated = boards.map((board) => {
    let boardChanged = false;
    const newCells = board.cells.map((row) =>
      row.map((cell) => {
        if (!cell || cell.type !== "category" || cell.id !== "cat-think") return cell;
        let cellChanged = false;
        const newUtterances = cell.utterances.map((u) => {
          const next = AR_THINK_REPLACEMENTS[u.text];
          if (next && next !== u.text) {
            cellChanged = true;
            return { ...u, text: next };
          }
          return u;
        });
        if (!cellChanged) return cell;
        boardChanged = true;
        return { ...cell, utterances: newUtterances };
      })
    );
    if (!boardChanged) return board;
    changed = true;
    return { ...board, cells: newCells, updatedAt: new Date().toISOString() };
  });
  return { boards: migrated, changed };
}

function migrateArabicLoveItems(
  boards: CommunicationBoardData[],
  language: Lang
): { boards: CommunicationBoardData[]; changed: boolean } {
  if (language !== "ar") return { boards, changed: false };
  let changed = false;
  const migrated = boards.map((board) => {
    let boardChanged = false;
    const newCells = board.cells.map((row) =>
      row.map((cell) => {
        if (!cell || cell.type !== "category" || cell.id !== "cat-love") return cell;
        let cellChanged = false;
        const newUtterances = cell.utterances.map((u) => {
          const next = AR_LOVE_REPLACEMENTS[u.text];
          if (next && next !== u.text) {
            cellChanged = true;
            return { ...u, text: next };
          }
          return u;
        });
        if (!cellChanged) return cell;
        boardChanged = true;
        return { ...cell, utterances: newUtterances };
      })
    );
    if (!boardChanged) return board;
    changed = true;
    return { ...board, cells: newCells, updatedAt: new Date().toISOString() };
  });
  return { boards: migrated, changed };
}

// Arabic cat-meeting language fixes (gender-neutral phrases).
const AR_MEETING_REPLACEMENTS: Record<string, string> = {
  "رائع": "ما أروعه",
  "سعيد لسماع ذلك": "يسعدني سماع ذلك",
  "سأكون سعيداً لو تأتي مجدداً": "يسعدني أن تأتي مرة أخرى",
  "آسف": "أنا آسف",
};

function migrateArabicMeetingItems(
  boards: CommunicationBoardData[],
  language: Lang
): { boards: CommunicationBoardData[]; changed: boolean } {
  if (language !== "ar") return { boards, changed: false };
  let changed = false;
  const migrated = boards.map((board) => {
    let boardChanged = false;
    const newCells = board.cells.map((row) =>
      row.map((cell) => {
        if (!cell || cell.type !== "category" || cell.id !== "cat-meeting") return cell;
        let cellChanged = false;
        const newUtterances = cell.utterances.map((u) => {
          const next = AR_MEETING_REPLACEMENTS[u.text];
          if (next && next !== u.text) {
            cellChanged = true;
            return { ...u, text: next };
          }
          return u;
        });
        if (!cellChanged) return cell;
        boardChanged = true;
        return { ...cell, utterances: newUtterances };
      })
    );
    if (!boardChanged) return board;
    changed = true;
    return { ...board, cells: newCells, updatedAt: new Date().toISOString() };
  });
  return { boards: migrated, changed };
}

// Arabic cat-sleep language fixes (gender-neutral phrases / wording corrections).
const AR_SLEEP_REPLACEMENTS: Record<string, string> = {
  "أنا متعب جداً": "أنا متعب جدًا",
  "أنا متعبة جداً": "أنا متعبة جدًا",
  "فقط أستريح": "أريد فقط أن أستريح",
  "أغير إلى البيجاما": "أريد أن أرتدي البيجاما",
  "بدون استحمام": "من دون استحمام",
  "أحتاج تنظيف الأسنان": "أحتاج إلى تنظيف أسناني",
  "لست مرتاحاً": "لست مرتاحًا",
  "إطفاء الضوء": "أطفئ الضوء",
  "إشعال الضوء": "شغّل الضوء",
  "الاستماع للموسيقى": "أستمع إلى الموسيقى",
  "مشاهدة التلفزيون": "أشاهد التلفزيون",
};

function migrateArabicSleepItems(
  boards: CommunicationBoardData[],
  language: Lang
): { boards: CommunicationBoardData[]; changed: boolean } {
  if (language !== "ar") return { boards, changed: false };
  let changed = false;
  const migrated = boards.map((board) => {
    let boardChanged = false;
    const newCells = board.cells.map((row) =>
      row.map((cell) => {
        if (!cell || cell.type !== "category" || cell.id !== "cat-sleep") return cell;
        let cellChanged = false;
        const newUtterances = cell.utterances.map((u) => {
          const next = AR_SLEEP_REPLACEMENTS[u.text];
          if (next && next !== u.text) {
            cellChanged = true;
            return { ...u, text: next };
          }
          return u;
        });
        if (!cellChanged) return cell;
        boardChanged = true;
        return { ...cell, utterances: newUtterances };
      })
    );
    if (!boardChanged) return board;
    changed = true;
    return { ...board, cells: newCells, updatedAt: new Date().toISOString() };
  });
  return { boards: migrated, changed };
}

// Arabic cat-meal language fixes (gender-neutral phrases / wording corrections).
const AR_MEAL_REPLACEMENTS: Record<string, string> = {
  "كافي": "يكفي",
  "لا أشعر بخير": "لست بخير",
};

function migrateArabicMealItems(
  boards: CommunicationBoardData[],
  language: Lang
): { boards: CommunicationBoardData[]; changed: boolean } {
  if (language !== "ar") return { boards, changed: false };
  let changed = false;
  const migrated = boards.map((board) => {
    let boardChanged = false;
    const newCells = board.cells.map((row) =>
      row.map((cell) => {
        if (!cell || cell.type !== "category" || cell.id !== "cat-meal") return cell;
        let cellChanged = false;
        const newUtterances = cell.utterances.map((u) => {
          const next = AR_MEAL_REPLACEMENTS[u.text];
          if (next && next !== u.text) {
            cellChanged = true;
            return { ...u, text: next };
          }
          return u;
        });
        if (!cellChanged) return cell;
        boardChanged = true;
        return { ...cell, utterances: newUtterances };
      })
    );
    if (!boardChanged) return board;
    changed = true;
    return { ...board, cells: newCells, updatedAt: new Date().toISOString() };
  });
  return { boards: migrated, changed };
}

// Arabic cat-outside language fixes (gender-neutral phrases / wording corrections).
const AR_OUTSIDE_REPLACEMENTS: Record<string, string> = {
  "ضجيج كثير هنا": "هناك ضجيج كثير هنا",
  "حار لي": "أشعر بالحر",
  "بارد لي": "أشعر بالبرد",
  "للبحر": "إلى الشاطئ",
  "للمشي في الشارع": "أمشي في الشارع",
};

function migrateArabicOutsideItems(
  boards: CommunicationBoardData[],
  language: Lang
): { boards: CommunicationBoardData[]; changed: boolean } {
  if (language !== "ar") return { boards, changed: false };
  let changed = false;
  const migrated = boards.map((board) => {
    let boardChanged = false;
    const newCells = board.cells.map((row) =>
      row.map((cell) => {
        if (!cell || cell.type !== "category" || cell.id !== "cat-outside") return cell;
        let cellChanged = false;
        const newUtterances = cell.utterances.map((u) => {
          const next = AR_OUTSIDE_REPLACEMENTS[u.text];
          if (next && next !== u.text) {
            cellChanged = true;
            return { ...u, text: next };
          }
          return u;
        });
        if (!cellChanged) return cell;
        boardChanged = true;
        return { ...cell, utterances: newUtterances };
      })
    );
    if (!boardChanged) return board;
    changed = true;
    return { ...board, cells: newCells, updatedAt: new Date().toISOString() };
  });
  return { boards: migrated, changed };
}

// Arabic gender-aware inner items: per-category mappings for female form.
// Only applied when user gender is female. Maps male form -> female form.
const AR_GENDER_REPLACEMENTS: Record<string, Record<string, string>> = {
  "cat-meeting": {
    "سعيد برؤيتك": "سعيدة برؤيتك",
    "أنا متعب": "أنا متعبة",
  },
  "cat-sleep": {
    "أنا متعب جدًا": "أنا متعبة جدًا",
    "لست مرتاحًا": "لست مرتاحةً",
    "أنا مرتاح": "أنا مرتاحة",
  },
  "cat-phrases": {
    "أنا متعب": "أنا متعبة",
    "أنا جائع": "أنا جائعة",
    "انا جائع": "أنا جائعة",
  },
};

function migrateArabicGenderItems(
  boards: CommunicationBoardData[],
  language: Lang,
  gender: "male" | "female" | "neutral"
): { boards: CommunicationBoardData[]; changed: boolean } {
  if (language !== "ar") return { boards, changed: false };
  if (gender !== "female" && gender !== "male") return { boards, changed: false };
  const isFemale = gender === "female";
  let changed = false;
  const migrated = boards.map((board) => {
    let boardChanged = false;
    const newCells = board.cells.map((row) =>
      row.map((cell) => {
        if (!cell || cell.type !== "category") return cell;
        const map = AR_GENDER_REPLACEMENTS[cell.id];
        if (!map) return cell;
        // Build forward (male->female) and reverse (female->male) maps.
        // Reverse only contains entries where the female form is unique to female
        // (i.e. the male form is different) — so we never accidentally rewrite
        // a male phrase that happens to be in the values.
        const forward = map; // male -> female
        const reverse: Record<string, string> = {};
        for (const [maleForm, femaleForm] of Object.entries(map)) {
          if (femaleForm !== maleForm) reverse[femaleForm] = maleForm;
        }
        const activeMap = isFemale ? forward : reverse;
        let cellChanged = false;
        const newUtterances = cell.utterances.map((u) => {
          const next = activeMap[u.text];
          if (next && next !== u.text) {
            cellChanged = true;
            return { ...u, text: next };
          }
          return u;
        });
        if (!cellChanged) return cell;
        boardChanged = true;
        return { ...cell, utterances: newUtterances };
      })
    );
    if (!boardChanged) return board;
    changed = true;
    return { ...board, cells: newCells, updatedAt: new Date().toISOString() };
  });
  return { boards: migrated, changed };
}

// Hebrew gender-aware inner items: auto-built from mainBoardLayout.
// For every category that defines `innerItemsFemale.he`, we pair each Hebrew male
// inner item with its female counterpart (only where they differ). Bidirectional:
// switching gender swaps the cell text both directions, leaving custom edits alone.
const HE_GENDER_REPLACEMENTS: Record<string, Record<string, string>> = (() => {
  const out: Record<string, Record<string, string>> = {};
  for (const row of mainBoardLayout) {
    for (const item of row) {
      if (
        !item ||
        item.type !== "category" ||
        !item.innerItems?.he ||
        !item.innerItemsFemale?.he
      ) continue;
      const males = item.innerItems.he;
      const females = item.innerItemsFemale.he;
      const len = Math.min(males.length, females.length);
      const map: Record<string, string> = {};
      for (let i = 0; i < len; i++) {
        if (males[i] !== females[i]) {
          map[males[i]] = females[i];
        }
      }
      if (Object.keys(map).length > 0) out[item.id] = map;
    }
  }
  return out;
})();

function migrateHebrewGenderItems(
  boards: CommunicationBoardData[],
  language: Lang,
  gender: "male" | "female" | "neutral"
): { boards: CommunicationBoardData[]; changed: boolean } {
  if (language !== "he") return { boards, changed: false };
  if (gender !== "female" && gender !== "male") return { boards, changed: false };
  const isFemale = gender === "female";
  let changed = false;
  const migrated = boards.map((board) => {
    let boardChanged = false;
    const newCells = board.cells.map((row) =>
      row.map((cell) => {
        if (!cell || cell.type !== "category") return cell;
        const map = HE_GENDER_REPLACEMENTS[cell.id];
        if (!map) return cell;
        const forward = map; // male -> female
        const reverse: Record<string, string> = {};
        for (const [maleForm, femaleForm] of Object.entries(map)) {
          if (femaleForm !== maleForm) reverse[femaleForm] = maleForm;
        }
        const activeMap = isFemale ? forward : reverse;
        let cellChanged = false;
        const newUtterances = cell.utterances.map((u) => {
          const next = activeMap[u.text];
          if (next && next !== u.text) {
            cellChanged = true;
            return { ...u, text: next };
          }
          return u;
        });
        if (!cellChanged) return cell;
        boardChanged = true;
        return { ...cell, utterances: newUtterances };
      })
    );
    if (!boardChanged) return board;
    changed = true;
    return { ...board, cells: newCells, updatedAt: new Date().toISOString() };
  });
  return { boards: migrated, changed };
}

// Russian inner item text replacements for cat-want (v45 migration)
const RU_WANT_REPLACEMENTS: Record<string, string> = {
  "туалет": "в туалет",
  "свет": "включить свет",
  "темноту": "выключить свет",
  "тишину": "тишины",
};

// Russian inner item text replacements for cat-need (position-agnostic, text-based)
// Maps any legacy phrasing to the canonical current text.
const RU_NEED_REPLACEMENTS: Record<string, string> = {
  // "чтобы мне помогли" — legacy variants
  "помогите": "чтобы мне помогли",
  "помощь": "чтобы мне помогли",
  "мне нужна помощь": "чтобы мне помогли",
  // "сделать аспирацию"
  "аспирация": "сделать аспирацию",
  "отсасывание": "сделать аспирацию",
  "сакшн": "сделать аспирацию",
  // "включить кондиционер"
  "кондиционер": "включить кондиционер",
  // "в туалет"
  "туалет": "в туалет",
  "в уборную": "в туалет",
  // "отдохнуть"
  "отдых": "отдохнуть",
  "отдыхать": "отдохнуть",
  // "побыть в тишине"
  "тишина": "побыть в тишине",
  "тишины": "побыть в тишине",
  "тишину": "побыть в тишине",
  "в тишине": "побыть в тишине",
  // "использовать слуховой аппарат"
  "слуховой аппарат": "использовать слуховой аппарат",
  "слуховые аппараты": "использовать слуховой аппарат",
  // "выключить свет"
  "темнота": "выключить свет",
  "темноту": "выключить свет",
  // "включить свет" — note: "свет" alone is ambiguous, handled in cat-want already
};

function migrateRussianNeedItems(
  boards: CommunicationBoardData[],
  language: Lang
): { boards: CommunicationBoardData[]; changed: boolean } {
  if (language !== "ru") return { boards, changed: false };
  let changed = false;

  const migrated = boards.map((board) => {
    let boardChanged = false;
    const newCells = board.cells.map((row) =>
      row.map((cell) => {
        if (!cell || cell.type !== "category" || cell.id !== "cat-need") return cell;
        const newUtterances = cell.utterances.map((u) => {
          const replacement = RU_NEED_REPLACEMENTS[u.text];
          if (replacement && replacement !== u.text) {
            boardChanged = true;
            return { ...u, text: replacement };
          }
          return u;
        });
        return { ...cell, utterances: newUtterances };
      })
    );
    if (!boardChanged) return board;
    changed = true;
    return { ...board, cells: newCells, updatedAt: new Date().toISOString() };
  });

  return { boards: migrated, changed };
}

// Russian inner item text replacements for cat-feel (text-based, gender-neutral)
const RU_FEEL_REPLACEMENTS: Record<string, string> = {
  "хорошо": "себя хорошо",
  "мне хорошо": "себя хорошо",
  "плохо": "себя плохо",
  "мне плохо": "себя плохо",
  "болит": "боль",
  "мне больно": "боль",
  "больно": "боль",
  "усталый": "усталость",
  "уставший": "усталость",
  "уставшая": "усталость",
  "тошнота": "тошноту",
  "тошнит": "тошноту",
  "меня тошнит": "тошноту",
  "холодно": "что мне холодно",
  "мне холодно": "что мне холодно",
  "жарко": "что мне жарко",
  "мне жарко": "что мне жарко",
  "давление": "давление в груди",
  "боль в груди": "давление в груди",
  "чешется": "зуд",
  "зудит": "зуд",
  "неудобно": "дискомфорт",
  "некомфортно": "дискомфорт",
  "очень хорошо": "себя очень хорошо",
  "мне очень хорошо": "себя очень хорошо",
  "нормально": "себя нормально",
  "мне нормально": "себя нормально",
  "ожидание": "предвкушение",
  "в ожидании": "предвкушение",
  "нетерпение": "раздражение",
  "раздражён": "раздражение",
  "раздражена": "раздражение",
  "не могу сосредоточиться": "трудности с концентрацией",
  "сложно сосредоточиться": "трудности с концентрацией",
  "невнимательность": "трудности с концентрацией",
  "рассеянность": "трудности с концентрацией",
};

function migrateRussianFeelItems(
  boards: CommunicationBoardData[],
  language: Lang
): { boards: CommunicationBoardData[]; changed: boolean } {
  if (language !== "ru") return { boards, changed: false };
  let changed = false;

  const migrated = boards.map((board) => {
    let boardChanged = false;
    const newCells = board.cells.map((row) =>
      row.map((cell) => {
        if (!cell || cell.type !== "category" || cell.id !== "cat-feel") return cell;
        const newUtterances = cell.utterances.map((u) => {
          const replacement = RU_FEEL_REPLACEMENTS[u.text];
          if (replacement && replacement !== u.text) {
            boardChanged = true;
            return { ...u, text: replacement };
          }
          return u;
        });
        return { ...cell, utterances: newUtterances };
      })
    );
    if (!boardChanged) return board;
    changed = true;
    return { ...board, cells: newCells, updatedAt: new Date().toISOString() };
  });

  return { boards: migrated, changed };
}

// Russian inner item text replacements for cat-think (text-based, gender-neutral)
const RU_THINK_REPLACEMENTS: Record<string, string> = {
  "хорошая идея": "это хорошая идея",
  "плохая идея": "это плохая идея",
  "может быть": "это возможно",
  "возможно": "это возможно",
  "не интересно": "это не интересно",
  "неинтересно": "это не интересно",
};

function migrateRussianThinkItems(
  boards: CommunicationBoardData[],
  language: Lang
): { boards: CommunicationBoardData[]; changed: boolean } {
  if (language !== "ru") return { boards, changed: false };
  let changed = false;

  const migrated = boards.map((board) => {
    let boardChanged = false;
    const newCells = board.cells.map((row) =>
      row.map((cell) => {
        if (!cell || cell.type !== "category" || cell.id !== "cat-think") return cell;
        const newUtterances = cell.utterances.map((u) => {
          const replacement = RU_THINK_REPLACEMENTS[u.text];
          if (replacement && replacement !== u.text) {
            boardChanged = true;
            return { ...u, text: replacement };
          }
          return u;
        });
        return { ...cell, utterances: newUtterances };
      })
    );
    if (!boardChanged) return board;
    changed = true;
    return { ...board, cells: newCells, updatedAt: new Date().toISOString() };
  });

  return { boards: migrated, changed };
}

// Russian inner item text replacements for cat-like (text-based)
const RU_LIKE_REPLACEMENTS: Record<string, string> = {
  "смотреть ТВ": "смотреть телевизор",
  "смотреть тв": "смотреть телевизор",
};

function migrateRussianLikeItems(
  boards: CommunicationBoardData[],
  language: Lang
): { boards: CommunicationBoardData[]; changed: boolean } {
  if (language !== "ru") return { boards, changed: false };
  let changed = false;

  const migrated = boards.map((board) => {
    let boardChanged = false;
    const newCells = board.cells.map((row) =>
      row.map((cell) => {
        if (!cell || cell.type !== "category" || cell.id !== "cat-like") return cell;
        const newUtterances = cell.utterances.map((u) => {
          const replacement = RU_LIKE_REPLACEMENTS[u.text];
          if (replacement && replacement !== u.text) {
            boardChanged = true;
            return { ...u, text: replacement };
          }
          return u;
        });
        return { ...cell, utterances: newUtterances };
      })
    );
    if (!boardChanged) return board;
    changed = true;
    return { ...board, cells: newCells, updatedAt: new Date().toISOString() };
  });

  return { boards: migrated, changed };
}

// Russian inner item text replacements for cat-dont-like (text-based)
const RU_DONT_LIKE_REPLACEMENTS: Record<string, string> = {
  "чувствовать так": "чувствовать себя так",
  "ситуацию": "такие ситуации",
  "ситуация": "такие ситуации",
  "запах": "такие запахи",
  "прикосновение": "прикосновения",
  "много людей": "когда много людей",
};

// Russian gender-aware inner items for cat-dont-like
const RU_DONT_LIKE_GENDER_REPLACEMENTS: Record<string, { male: string; female: string }> = {
  "быть одному": { male: "быть один", female: "быть одна" },
  "быть одной": { male: "быть один", female: "быть одна" },
  "быть один": { male: "быть один", female: "быть одна" },
  "быть одна": { male: "быть один", female: "быть одна" },
};

function migrateRussianDontLikeItems(
  boards: CommunicationBoardData[],
  language: Lang,
  gender: "male" | "female" | "neutral"
): { boards: CommunicationBoardData[]; changed: boolean } {
  if (language !== "ru") return { boards, changed: false };
  const isFemale = gender === "female";
  let changed = false;

  const migrated = boards.map((board) => {
    let boardChanged = false;
    const newCells = board.cells.map((row) =>
      row.map((cell) => {
        if (!cell || cell.type !== "category" || cell.id !== "cat-dont-like") return cell;
        const newUtterances = cell.utterances.map((u) => {
          let newText = u.text;
          if (RU_DONT_LIKE_REPLACEMENTS[newText]) {
            newText = RU_DONT_LIKE_REPLACEMENTS[newText];
          }
          const genderEntry =
            RU_DONT_LIKE_GENDER_REPLACEMENTS[u.text] ||
            RU_DONT_LIKE_GENDER_REPLACEMENTS[newText];
          if (genderEntry) {
            newText = isFemale ? genderEntry.female : genderEntry.male;
          }
          if (newText !== u.text) {
            boardChanged = true;
            return { ...u, text: newText };
          }
          return u;
        });
        return { ...cell, utterances: newUtterances };
      })
    );
    if (!boardChanged) return board;
    changed = true;
    return { ...board, cells: newCells, updatedAt: new Date().toISOString() };
  });

  return { boards: migrated, changed };
}

// Russian inner item text replacements for cat-meeting (text-based, gender-neutral)
const RU_MEETING_REPLACEMENTS: Record<string, string> = {
  "расскажи ещё об этом": "расскажи об этом подробнее",
  "ой-ой": "о нет",
};

// Russian gender-aware inner items for cat-meeting
const RU_MEETING_GENDER_REPLACEMENTS: Record<string, { male: string; female: string }> = {
  "рад тебя видеть": { male: "рад тебя видеть", female: "рада тебя видеть" },
  "рада тебя видеть": { male: "рад тебя видеть", female: "рада тебя видеть" },
  "рад слышать": { male: "рад слышать", female: "рада слышать" },
  "рада слышать": { male: "рад слышать", female: "рада слышать" },
  "я не понял": { male: "я не понял", female: "я не поняла" },
  "я не поняла": { male: "я не понял", female: "я не поняла" },
  "буду рад если придёшь снова": { male: "буду рад, если придёшь снова", female: "буду рада, если придёшь снова" },
  "буду рада если придёшь снова": { male: "буду рад, если придёшь снова", female: "буду рада, если придёшь снова" },
  "буду рад, если придёшь снова": { male: "буду рад, если придёшь снова", female: "буду рада, если придёшь снова" },
  "буду рада, если придёшь снова": { male: "буду рад, если придёшь снова", female: "буду рада, если придёшь снова" },
  "я не подумал об этом": { male: "я не подумал об этом", female: "я не подумала об этом" },
  "я не подумала об этом": { male: "я не подумал об этом", female: "я не подумала об этом" },
};

function migrateRussianMeetingItems(
  boards: CommunicationBoardData[],
  language: Lang,
  gender: "male" | "female" | "neutral"
): { boards: CommunicationBoardData[]; changed: boolean } {
  if (language !== "ru") return { boards, changed: false };
  const isFemale = gender === "female";
  let changed = false;

  const migrated = boards.map((board) => {
    let boardChanged = false;
    const newCells = board.cells.map((row) =>
      row.map((cell) => {
        if (!cell || cell.type !== "category" || cell.id !== "cat-meeting") return cell;
        const newUtterances = cell.utterances.map((u) => {
          let newText = u.text;
          if (RU_MEETING_REPLACEMENTS[newText]) {
            newText = RU_MEETING_REPLACEMENTS[newText];
          }
          const genderEntry =
            RU_MEETING_GENDER_REPLACEMENTS[u.text] ||
            RU_MEETING_GENDER_REPLACEMENTS[newText];
          if (genderEntry) {
            newText = isFemale ? genderEntry.female : genderEntry.male;
          }
          if (newText !== u.text) {
            boardChanged = true;
            return { ...u, text: newText };
          }
          return u;
        });
        return { ...cell, utterances: newUtterances };
      })
    );
    if (!boardChanged) return board;
    changed = true;
    return { ...board, cells: newCells, updatedAt: new Date().toISOString() };
  });

  return { boards: migrated, changed };
}

// Russian cat-sleep inner item replacements (gender-neutral)
const RU_SLEEP_REPLACEMENTS: Record<string, string> = {
  "смотреть ТВ": "посмотреть телевизор",
  "смотреть тв": "посмотреть телевизор",
  "смотреть телевизор": "посмотреть телевизор",
};

function migrateRussianSleepItems(
  boards: CommunicationBoardData[],
  language: Lang
): { boards: CommunicationBoardData[]; changed: boolean } {
  if (language !== "ru") return { boards, changed: false };
  let changed = false;

  const migrated = boards.map((board) => {
    let boardChanged = false;
    const newCells = board.cells.map((row) =>
      row.map((cell) => {
        if (!cell || cell.type !== "category" || cell.id !== "cat-sleep") return cell;
        const newUtterances = cell.utterances.map((u) => {
          const replacement = RU_SLEEP_REPLACEMENTS[u.text];
          if (replacement && replacement !== u.text) {
            boardChanged = true;
            return { ...u, text: replacement };
          }
          return u;
        });
        return { ...cell, utterances: newUtterances };
      })
    );
    if (!boardChanged) return board;
    changed = true;
    return { ...board, cells: newCells, updatedAt: new Date().toISOString() };
  });

  return { boards: migrated, changed };
}

// Russian cat-phrases inner item replacements (gender-neutral)
const RU_PHRASES_REPLACEMENTS: Record<string, string> = {
  "когда придёшь?": "когда ты придёшь?",
};

function migrateRussianPhrasesItems(
  boards: CommunicationBoardData[],
  language: Lang
): { boards: CommunicationBoardData[]; changed: boolean } {
  if (language !== "ru") return { boards, changed: false };
  let changed = false;

  const migrated = boards.map((board) => {
    let boardChanged = false;
    const newCells = board.cells.map((row) =>
      row.map((cell) => {
        if (!cell || cell.type !== "category" || cell.id !== "cat-phrases") return cell;
        const newUtterances = cell.utterances.map((u) => {
          const replacement = RU_PHRASES_REPLACEMENTS[u.text];
          if (replacement && replacement !== u.text) {
            boardChanged = true;
            return { ...u, text: replacement };
          }
          return u;
        });
        return { ...cell, utterances: newUtterances };
      })
    );
    if (!boardChanged) return board;
    changed = true;
    return { ...board, cells: newCells, updatedAt: new Date().toISOString() };
  });

  return { boards: migrated, changed };
}

// Russian cat-outside inner item replacements (gender-neutral)
const RU_OUTSIDE_REPLACEMENTS: Record<string, string> = {
  "куда идём?": "куда мы идём?",
  "когда вернёмся?": "когда мы вернёмся?",
  "можно вернуться?": "мы можем вернуться?",
  "на море": "на пляж",
};

function migrateRussianOutsideItems(
  boards: CommunicationBoardData[],
  language: Lang
): { boards: CommunicationBoardData[]; changed: boolean } {
  if (language !== "ru") return { boards, changed: false };
  let changed = false;

  const migrated = boards.map((board) => {
    let boardChanged = false;
    const newCells = board.cells.map((row) =>
      row.map((cell) => {
        if (!cell || cell.type !== "category" || cell.id !== "cat-outside") return cell;
        const newUtterances = cell.utterances.map((u) => {
          const replacement = RU_OUTSIDE_REPLACEMENTS[u.text];
          if (replacement && replacement !== u.text) {
            boardChanged = true;
            return { ...u, text: replacement };
          }
          return u;
        });
        return { ...cell, utterances: newUtterances };
      })
    );
    if (!boardChanged) return board;
    changed = true;
    return { ...board, cells: newCells, updatedAt: new Date().toISOString() };
  });

  return { boards: migrated, changed };
}

// Russian gender-aware inner items
const RU_GENDER_REPLACEMENTS: Record<string, { male: string; female: string }> = {
  "побыть одному": { male: "побыть один", female: "побыть одна" },
  "побыть одной": { male: "побыть один", female: "побыть одна" },
  "побыть один": { male: "побыть один", female: "побыть одна" },
  "побыть одна": { male: "побыть один", female: "побыть одна" },
  "устал": { male: "устал", female: "устала" },
  "я устал": { male: "я устал", female: "я устала" },
  "я голоден": { male: "я голоден", female: "я голодна" },
  "я голодна": { male: "я голоден", female: "я голодна" },
  "очень устал": { male: "очень устал", female: "очень устала" },
  "я закончил": { male: "я закончил", female: "я закончила" },
  "я закончила": { male: "я закончил", female: "я закончила" },
};

function migrateRussianInnerItems(
  boards: CommunicationBoardData[],
  language: Lang,
  gender: "male" | "female" | "neutral"
): { boards: CommunicationBoardData[]; changed: boolean } {
  if (language !== "ru") return { boards, changed: false };
  const isFemale = gender === "female";
  let changed = false;

  const migrated = boards.map((board) => {
    let boardChanged = false;
    const newCells = board.cells.map((row) =>
      row.map((cell) => {
        if (!cell || cell.type !== "category" || (cell.id !== "cat-want" && cell.id !== "cat-feel" && cell.id !== "cat-phrases" && cell.id !== "cat-sleep" && cell.id !== "cat-meeting" && cell.id !== "cat-meal")) return cell;
        const newUtterances = cell.utterances.map((u) => {
          let newText = u.text;
          // Apply text replacements
          if (RU_WANT_REPLACEMENTS[newText]) {
            newText = RU_WANT_REPLACEMENTS[newText];
          }
          // Apply gender replacements
          const genderEntry = RU_GENDER_REPLACEMENTS[u.text] || RU_GENDER_REPLACEMENTS[newText];
          if (genderEntry) {
            newText = isFemale ? genderEntry.female : genderEntry.male;
          }
          if (newText !== u.text) {
            boardChanged = true;
            return { ...u, text: newText };
          }
          return u;
        });
        return { ...cell, utterances: newUtterances };
      })
    );
    if (!boardChanged) return board;
    changed = true;
    return { ...board, cells: newCells, updatedAt: new Date().toISOString() };
  });

  return { boards: migrated, changed };
}

function migrateGenderNames(
  boards: CommunicationBoardData[],
  language: Lang,
  gender: "male" | "female" | "neutral"
): { boards: CommunicationBoardData[]; changed: boolean } {
  // Gender names only apply to Hebrew (category names)
  if (language !== "he") return { boards, changed: false };

  const isFemale = gender === "female";
  let changed = false;

  const migrated = boards.map((board) => {
    let boardChanged = false;
    const newCells = board.cells.map((row) =>
      row.map((cell) => {
        if (!cell || cell.type !== "category") return cell;
        const genderNames = GREEN_CATEGORY_GENDER_NAMES[cell.id];
        if (!genderNames) return cell;

        const expectedName = isFemale ? genderNames.female : genderNames.male;
        const oppositeName = isFemale ? genderNames.male : genderNames.female;

        // Only update if the name matches the opposite gender (avoid overwriting custom names)
        if (cell.name === oppositeName) {
          boardChanged = true;
          return { ...cell, name: expectedName };
        }
        return cell;
      })
    );

    if (!boardChanged) return board;
    changed = true;
    return { ...board, cells: newCells, updatedAt: new Date().toISOString() };
  });

  return { boards: migrated, changed };
}

function arraysEqual(a: string[], b: string[]) {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    if (a[i] !== b[i]) return false;
  }
  return true;
}

function categoryTexts(category: CategoryCell): string[] {
  return category.utterances.map((u) => u.text);
}

function buildDefaultCategoryTextsById(language: Lang): Map<string, string[]> {
  const defaultBoard = getNewDefaultBoard(language);
  const byId = new Map<string, string[]>();

  for (const row of defaultBoard.cells) {
    for (const cell of row) {
      if (cell?.type === "category") {
        byId.set(cell.id, cell.utterances.map((u) => u.text));
      }
    }
  }

  return byId;
}

// Legacy defaults (before recent updates). We only auto-migrate when the stored
// category EXACTLY matches these legacy lists, to avoid overwriting caregiver edits.
const LEGACY_YELLOW_CATEGORIES: Record<string, Record<Lang, string[]>> = {
  "cat-sleep": {
    he: [
      "עייף",
      "רוצה לישון",
      "לא יכול לישון",
      "התעוררתי",
      "חלום",
      "סיוט",
      "צריך שמיכה",
      "קר לי",
      "חם לי",
      "רוצה אור",
    ],
    en: [
      "tired",
      "want to sleep",
      "can't sleep",
      "woke up",
      "dream",
      "nightmare",
      "need blanket",
      "I'm cold",
      "I'm hot",
      "want light",
    ],
    ru: [
      "устал",
      "хочу спать",
      "не могу спать",
      "проснулся",
      "сон",
      "кошмар",
      "нужно одеяло",
      "мне холодно",
      "мне жарко",
      "хочу свет",
    ],
    ar: [
      "متعب",
      "أريد أن أنام",
      "لا أستطيع النوم",
      "استيقظت",
      "حلم",
      "كابوس",
      "أحتاج بطانية",
      "أشعر بالبرد",
      "أشعر بالحر",
      "أريد ضوء",
    ],
  },
  "cat-meal": {
    he: [
      "ארוחת בוקר",
      "ארוחת צהריים",
      "ארוחת ערב",
      "חטיף",
      "מים",
      "תה",
      "קפה",
      "מיץ",
      "פירות",
      "ירקות",
    ],
    en: [
      "breakfast",
      "lunch",
      "dinner",
      "snack",
      "water",
      "tea",
      "coffee",
      "juice",
      "fruit",
      "vegetables",
    ],
    ru: [
      "завтрак",
      "обед",
      "ужин",
      "перекус",
      "вода",
      "чай",
      "кофе",
      "сок",
      "фрукты",
      "овощи",
    ],
    ar: [
      "فطور",
      "غداء",
      "عشاء",
      "وجبة خفيفة",
      "ماء",
      "شاي",
      "قهوة",
      "عصير",
      "فواكه",
      "خضروات",
    ],
  },
  "cat-outside": {
    he: [
      "לא",
      "כן",
      "בבקשה",
      "תודה",
      "יש כאן יותר מדי רעש",
      "חם לי",
      "קר לי",
      "נעים לי",
      "לאן הולכים\u200F?",
      "מתי חוזרים\u200F?",
      "מה זה\u200F?",
      "רוצה לעצור כאן",
      "רוצה ללכת למקום אחר",
      "זה מפריע לי",
      "לאט יותר",
      "מהר יותר",
      "אפשר לחזור\u200F?",
      "לא נוח לי",
    ],
    en: [
      "no",
      "yes",
      "please",
      "thank you",
      "too much noise here",
      "I'm hot",
      "I'm cold",
      "I'm comfortable",
      "where are we going?",
      "when are we coming back?",
      "what is this?",
      "want to stop here",
      "want to go somewhere else",
      "this bothers me",
      "slower",
      "faster",
      "can we go back?",
      "I'm not comfortable",
    ],
    ru: [
      "нет",
      "да",
      "пожалуйста",
      "спасибо",
      "здесь слишком шумно",
      "мне жарко",
      "мне холодно",
      "мне хорошо",
      "куда идём?",
      "когда вернёмся?",
      "что это?",
      "хочу остановиться здесь",
      "хочу в другое место",
      "это мне мешает",
      "медленнее",
      "быстрее",
      "можно вернуться?",
      "мне неудобно",
    ],
    ar: [
      "لا",
      "نعم",
      "من فضلك",
      "شكراً",
      "ضجيج كثير هنا",
      "حار لي",
      "بارد لي",
      "مرتاح",
      "إلى أين نذهب؟",
      "متى نعود؟",
      "ما هذا؟",
      "أريد التوقف هنا",
      "أريد الذهاب لمكان آخر",
      "هذا يزعجني",
      "أبطأ",
      "أسرع",
      "هل يمكننا العودة؟",
      "لست مرتاحاً",
    ],
  },
  "cat-topics": {
    he: [
      "משפחה",
      "בריאות",
      "כסף",
      "עבודה",
      "חדשות",
      "מזג אוויר",
      "טלוויזיה",
      "ספורט",
      "זמן",
      "תוכניות",
    ],
    en: [
      "family",
      "health",
      "money",
      "work",
      "news",
      "weather",
      "TV",
      "sports",
      "time",
      "plans",
    ],
    ru: [
      "семья",
      "здоровье",
      "деньги",
      "работа",
      "новости",
      "погода",
      "телевизор",
      "спорт",
      "время",
      "планы",
    ],
    ar: [
      "العائلة",
      "الصحة",
      "المال",
      "العمل",
      "الأخبار",
      "الطقس",
      "التلفزيون",
      "الرياضة",
      "الوقت",
      "الخطط",
    ],
  },
};

function replaceCategoryUtterances(
  category: CategoryCell,
  texts: string[]
): CategoryCell {
  const newUtterances: UtteranceCell[] = texts.map((text, i) => ({
    id: `${category.id}-item-${i}`,
    type: "utterance",
    text,
    color: category.color,
  }));

  return {
    ...category,
    utterances: newUtterances,
  };
}

// Emoji migrations: old emoji -> new emoji for specific positions
// Format: { categoryId: { itemIndex: { oldEmoji: newEmoji } } }
const EMOJI_MIGRATIONS: Record<string, Record<number, { from: string; to: string }>> = {
  "cat-feel": {
    14: { from: "🌟", to: "🌈" }, // אופטימיות: star -> rainbow
  },
};

function migrateEmojis(
  boards: CommunicationBoardData[]
): { boards: CommunicationBoardData[]; changed: boolean } {
  let changed = false;

  const migrated = boards.map((board) => {
    let boardChanged = false;

    const newCells = board.cells.map((row) =>
      row.map((cell) => {
        if (!cell || cell.type !== "category") return cell;

        const emojiMigrations = EMOJI_MIGRATIONS[cell.id];
        if (!emojiMigrations) return cell;

        let categoryChanged = false;
        const newUtterances = cell.utterances.map((utterance, index) => {
          const migration = emojiMigrations[index];
          if (migration && utterance.emoji === migration.from) {
            categoryChanged = true;
            return { ...utterance, emoji: migration.to };
          }
          return utterance;
        });

        if (!categoryChanged) return cell;
        boardChanged = true;
        return { ...cell, utterances: newUtterances };
      })
    );

    if (!boardChanged) return board;
    changed = true;
    return {
      ...board,
      cells: newCells,
      updatedAt: new Date().toISOString(),
    };
  });

  return { boards: migrated, changed };
}

/**
 * Replace cat-topics with cat-phrases (migration: topics → phrases)
 */
function migrateTopicsToPhrases(
  boards: CommunicationBoardData[],
  language: Lang,
  gender: "male" | "female" | "neutral" = "neutral"
): { boards: CommunicationBoardData[]; changed: boolean } {
  let changed = false;
  const defaultBoard = getNewDefaultBoard(language, gender);

  // Find cat-phrases in default board
  let defaultPhraseCell: CategoryCell | null = null;
  for (const row of defaultBoard.cells) {
    for (const cell of row) {
      if (cell?.type === "category" && cell.id === "cat-phrases") {
        defaultPhraseCell = cell;
        break;
      }
    }
    if (defaultPhraseCell) break;
  }

  if (!defaultPhraseCell) return { boards, changed };

  const migrated = boards.map((board) => {
    let boardChanged = false;
    const newCells = board.cells.map((row) =>
      row.map((cell) => {
        if (!cell || cell.type !== "category" || cell.id !== "cat-topics") return cell;
        boardChanged = true;
        return defaultPhraseCell!;
      })
    );

    if (!boardChanged) return board;
    changed = true;
    return { ...board, cells: newCells, updatedAt: new Date().toISOString() };
  });

  return { boards: migrated, changed };
}

export function migrateCommunicationBoards(
  boards: CommunicationBoardData[],
  language: Lang,
  gender: "male" | "female" | "neutral" = "neutral"
): { boards: CommunicationBoardData[]; changed: boolean } {
  const defaultsById = buildDefaultCategoryTextsById(language);

  let changed = false;
  let currentBoards = boards;

  // First pass: migrate yellow categories (text content)
  const yellowResult = currentBoards.map((board) => {
    let boardChanged = false;

    const newCells = board.cells.map((row) =>
      row.map((cell) => {
        if (!cell || cell.type !== "category") return cell;

        const legacy = LEGACY_YELLOW_CATEGORIES[cell.id]?.[language];
        const nextTexts = defaultsById.get(cell.id);
        if (!legacy || !nextTexts) return cell;

        const currentTexts = categoryTexts(cell);
        if (!arraysEqual(currentTexts, legacy)) return cell;

        boardChanged = true;
        return replaceCategoryUtterances(cell, nextTexts);
      })
    );

    if (!boardChanged) return board;
    changed = true;
    return {
      ...board,
      cells: newCells,
      updatedAt: new Date().toISOString(),
    };
  });

  currentBoards = yellowResult;

  // Second pass: migrate emojis
  const emojiResult = migrateEmojis(currentBoards);
  if (emojiResult.changed) {
    changed = true;
    currentBoards = emojiResult.boards;
  }

  // Third pass: replace cat-topics with cat-phrases
  const topicsResult = migrateTopicsToPhrases(currentBoards, language, gender);
  if (topicsResult.changed) {
    changed = true;
    currentBoards = topicsResult.boards;
  }

  // Fourth pass: fix gender-aware verb category names
  const genderResult = migrateGenderNames(currentBoards, language, gender);
  if (genderResult.changed) {
    changed = true;
    currentBoards = genderResult.boards;
  }

  // Fifth pass: fix Russian inner items and apply Russian gender
  const ruResult = migrateRussianInnerItems(currentBoards, language, gender);
  if (ruResult.changed) {
    changed = true;
    currentBoards = ruResult.boards;
  }

  // Sixth pass: fix Arabic green category names (legacy placeholder text)
  const arNamesResult = migrateArabicGreenNames(currentBoards, language);
  if (arNamesResult.changed) {
    changed = true;
    currentBoards = arNamesResult.boards;
  }

  // Seventh pass: fix Arabic connector texts (row 4 gray cells)
  const arConnResult = migrateArabicConnectors(currentBoards, language);
  if (arConnResult.changed) {
    changed = true;
    currentBoards = arConnResult.boards;
  }

  // Eighth pass: fix Arabic cat-think inner items
  const arThinkResult = migrateArabicThinkItems(currentBoards, language);
  if (arThinkResult.changed) {
    changed = true;
    currentBoards = arThinkResult.boards;
  }

  // Ninth pass: fix Arabic cat-love inner items
  const arLoveResult = migrateArabicLoveItems(currentBoards, language);
  if (arLoveResult.changed) {
    changed = true;
    currentBoards = arLoveResult.boards;
  }

  // Tenth-A pass: fix Arabic cat-sleep wording (must run BEFORE gender pass so keys match)
  const arSleepResult = migrateArabicSleepItems(currentBoards, language);
  if (arSleepResult.changed) {
    changed = true;
    currentBoards = arSleepResult.boards;
  }

  // Tenth-B pass: fix Arabic cat-meal wording (gender-neutral)
  const arMealResult = migrateArabicMealItems(currentBoards, language);
  if (arMealResult.changed) {
    changed = true;
    currentBoards = arMealResult.boards;
  }

  // Tenth-C pass: fix Arabic cat-outside wording (gender-neutral)
  const arOutsideResult = migrateArabicOutsideItems(currentBoards, language);
  if (arOutsideResult.changed) {
    changed = true;
    currentBoards = arOutsideResult.boards;
  }

  // Tenth pass: apply Arabic gender-aware inner items (female forms)
  const arGenderResult = migrateArabicGenderItems(currentBoards, language, gender);
  if (arGenderResult.changed) {
    changed = true;
    currentBoards = arGenderResult.boards;
  }

  // Eleventh pass: fix Arabic cat-meeting language (gender-neutral)
  const arMeetingResult = migrateArabicMeetingItems(currentBoards, language);
  if (arMeetingResult.changed) {
    changed = true;
    currentBoards = arMeetingResult.boards;
  }

  // Twelfth pass: apply Hebrew gender-aware inner items (bidirectional)
  const heGenderResult = migrateHebrewGenderItems(currentBoards, language, gender);
  if (heGenderResult.changed) {
    changed = true;
    currentBoards = heGenderResult.boards;
  }

  // Twelfth-B pass: fix Russian connector texts (row 4 gray cells)
  const ruConnResult = migrateRussianConnectors(currentBoards, language);
  if (ruConnResult.changed) {
    changed = true;
    currentBoards = ruConnResult.boards;
  }

  // Twelfth-C pass: fix Russian cat-need inner item texts
  const ruNeedResult = migrateRussianNeedItems(currentBoards, language);
  if (ruNeedResult.changed) {
    changed = true;
    currentBoards = ruNeedResult.boards;
  }

  // Twelfth-D pass: fix Russian cat-feel inner item texts
  const ruFeelResult = migrateRussianFeelItems(currentBoards, language);
  if (ruFeelResult.changed) {
    changed = true;
    currentBoards = ruFeelResult.boards;
  }

  // Twelfth-E pass: fix Russian cat-think inner item texts
  const ruThinkResult = migrateRussianThinkItems(currentBoards, language);
  if (ruThinkResult.changed) {
    changed = true;
    currentBoards = ruThinkResult.boards;
  }

  // Twelfth-F pass: fix Russian cat-like inner item texts
  const ruLikeResult = migrateRussianLikeItems(currentBoards, language);
  if (ruLikeResult.changed) {
    changed = true;
    currentBoards = ruLikeResult.boards;
  }

  // Twelfth-G pass: fix Russian cat-dont-like inner item texts (with gender)
  const ruDontLikeResult = migrateRussianDontLikeItems(currentBoards, language, gender);
  if (ruDontLikeResult.changed) {
    changed = true;
    currentBoards = ruDontLikeResult.boards;
  }

  // Twelfth-H pass: fix Russian cat-meeting inner item texts (with gender)
  const ruMeetingResult = migrateRussianMeetingItems(currentBoards, language, gender);
  if (ruMeetingResult.changed) {
    changed = true;
    currentBoards = ruMeetingResult.boards;
  }

  // Twelfth-I pass: fix Russian cat-sleep inner item texts
  const ruSleepResult = migrateRussianSleepItems(currentBoards, language);
  if (ruSleepResult.changed) {
    changed = true;
    currentBoards = ruSleepResult.boards;
  }

  // Twelfth-J pass: fix Russian cat-outside inner item texts
  const ruOutsideResult = migrateRussianOutsideItems(currentBoards, language);
  if (ruOutsideResult.changed) {
    changed = true;
    currentBoards = ruOutsideResult.boards;
  }

  // Twelfth-K pass: fix Russian cat-phrases inner item texts
  const ruPhrasesResult = migrateRussianPhrasesItems(currentBoards, language);
  if (ruPhrasesResult.changed) {
    changed = true;
    currentBoards = ruPhrasesResult.boards;
  }

  // Twelfth-L pass: fix English cell texts (e.g. pronoun-you-f from "you" to "we")
  const enCellResult = migrateEnglishCellTexts(currentBoards, language);
  if (enCellResult.changed) {
    changed = true;
    currentBoards = enCellResult.boards;
  }

  return { boards: currentBoards, changed };
}
